// src/pages/customer/CustomerCatalog.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { favorites as favApi, shop as shopApi } from '../../api';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { useToast } from '../../hooks/useToast';
import { CUSTOMER_NAV, useCustomerNav } from './CustomerHome';

export default function CustomerCatalog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();
  const navItems = useCustomerNav();
  const [favs, setFavs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchId, setSearchId] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);

  useEffect(() => { loadFavs(); }, []);

  const loadFavs = async () => {
    setLoading(true);
    try { setFavs(await favApi.list()); } catch (e) {}
    setLoading(false);
  };

  const handleSearch = async () => {
    const id = searchId.trim();
    if (!id) return;
    setSearching(true);
    setSearchResult(null);
    try {
      const data = await shopApi.getById(id);
      setSearchResult(data);
    } catch (e) {
      show(e.message || t('customerCatalog.shopNotFound'), 'error');
    }
    setSearching(false);
  };

  const handleAddToFavs = async () => {
    if (!searchResult) return;
    try {
      await favApi.add(searchResult.id);
      show(t('customerCatalog.shopAdded'));
      setSearchId(''); setSearchResult(null);
      await loadFavs();
    } catch (e) { show(e.message, 'error'); }
  };

  const handleRemove = (e, shopId) => {
    e.stopPropagation();
    if (!window.confirm(t('customerCatalog.confirmRemoveFav'))) return;
    favApi.remove(shopId);
    setFavs(favs.filter(s => s.id !== shopId));
  };

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  const alreadyFav = searchResult && favApi.has(searchResult.id);

  return (
    <div className="page">
      {ToastView}
      <div className="page-header">
        <div>
          <p className="page-subtitle">{t('nav.catalog').toUpperCase()}</p>
          <h1 className="page-title">{t('customerCatalog.title')}</h1>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Поиск по ID */}
        <div className="card" style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{t("customerCatalog.findByID")}</p>
          <p style={{ fontSize: 12, color: 'var(--fg-faint)', marginBottom: 12 }}>
            {t("customerCatalog.idHint")}
          </p>
          <input
            className="input"
            placeholder={t("customerCatalog.idPlaceholder")}
            value={searchId}
            onChange={e => setSearchId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 14, letterSpacing: 1, marginBottom: 10 }}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <button className="btn btn-primary" onClick={handleSearch}
            disabled={searching || !searchId.trim()}>
            {searching ? t('customerCatalog.searching') : t('customerCatalog.findByID')}
          </button>

          {searchResult && (
            <div className="fade-up" style={{
              marginTop: 14, padding: 12,
              background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)',
            }}>
              <p style={{ fontSize: 15, fontWeight: 700 }}>{searchResult.name}</p>
              {searchResult.description && (
                <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>
                  {searchResult.description}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/shop/${searchResult.id}`)} style={{ flex: 1 }}>
                  {t('customerCatalog.open')}
                </button>
                {alreadyFav ? (
                  <button className="btn btn-secondary btn-sm" disabled style={{ flex: 1, opacity: 0.6 }}>
                    <Icon name="check" size={14} stroke={2.5} />
                    {t('customerCatalog.inFavs')}
                  </button>
                ) : (
                  <button className="btn btn-primary btn-sm" onClick={handleAddToFavs} style={{ flex: 1 }}>
                    <Icon name="plus" size={14} stroke={2.5} />
                    {t('customerCatalog.addToFavs')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 10, letterSpacing: '0.08em' }}>
          {t("customerCatalog.favorites")} {favs.length > 0 && <span style={{ color: 'var(--fg-faint)' }}>· {favs.length}</span>}
        </p>

        {favs.length === 0 ? (
          <div className="empty-state">
            <div className="icon"><Icon name="star" size={28} stroke={1.5} /></div>
            <p>{t("customerCatalog.noFavs")}<br/>{t("customerCatalog.noFavsHint")}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {favs.map(s => (
              <div key={s.id} className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/shop/${s.id}`)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  {s.photo ? (
                    <img src={s.photo} alt="" style={{ width: 48, height: 48, borderRadius: 'var(--r-sm)', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{
                      width: 48, height: 48, borderRadius: 'var(--r-sm)',
                      background: 'linear-gradient(135deg, var(--brand-primary-soft), var(--brand-accent-soft))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'var(--brand-primary)', flexShrink: 0,
                    }}><Icon name="store" size={20} /></div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.name}
                    </p>
                    {s.description && (
                      <p style={{ fontSize: 12, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.description}
                      </p>
                    )}
                    <p style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
                      {t('customerCatalog.idLabel')} {s.id}{s._stale && ' · ' + t('customerCatalog.offline')}
                    </p>
                  </div>
                  <button onClick={(e) => handleRemove(e, s.id)}
                    style={{
                      background: 'transparent', border: '1px solid var(--border)',
                      color: 'var(--fg-faint)', borderRadius: 'var(--r-xs)',
                      width: 32, height: 32, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                    <Icon name="trash" size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav items={navItems} />
    </div>
  );
}
